/**
 * Share a segment: only me, everyone in the organisation, or chosen people.
 *
 * Only me is a private segment, kept in the person's own saved tabs. Either
 * of the others moves it to the shared list, and choosing Only me again
 * moves it back to its owner (see routes/v1/shared_segments.rs). What is
 * shared is the filters: everyone who opens it sees only the rows their own
 * login can see, which the note under the choices says in plain words.
 */
import { useState } from 'react';
import {
  Alert, Button, Group, Loader, Modal, MultiSelect, Radio, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  useCreateSharedSegment, useMakeSegmentPrivate, useSegmentPeople, useUpdateSharedSegment,
  type FilterTab, type SegmentVisibility, type SharedSegment,
} from '../api/queries';
import { toFilters } from '../api/filterState';
import { activeFilterCount } from './leadsFilterModel';
import { roleLabel, useVocab } from '../vocab';

export type ShareTarget =
  | { kind: 'private'; tab: FilterTab }
  | { kind: 'shared'; segment: SharedSegment };

type Choice = 'private' | SegmentVisibility;

/** Where the segment ended up, so the page can keep it open. */
export type ShareResult =
  | { kind: 'shared'; segment: SharedSegment }
  | { kind: 'private'; tabId: number }
  | { kind: 'unchanged' };

export function ShareSegmentModal({ target, onClose, onDone }: {
  target: ShareTarget | null;
  onClose: () => void;
  onDone?: (result: ShareResult) => void;
}) {
  const key = target ? (target.kind === 'private' ? `p${target.tab.id}` : `s${target.segment.id}`) : 'none';
  return (
    <Modal opened={target !== null} onClose={onClose} title="Share segment" size="md">
      {/* Keyed, so reopening on another segment starts from that segment's sharing. */}
      {target && <ShareForm key={key} target={target} onClose={onClose} onDone={onDone} />}
    </Modal>
  );
}

function ShareForm({ target, onClose, onDone }: {
  target: ShareTarget;
  onClose: () => void;
  onDone?: (result: ShareResult) => void;
}) {
  const v = useVocab();
  const shared = target.kind === 'shared' ? target.segment : null;
  const [choice, setChoice] = useState<Choice>(shared ? shared.visibility : 'private');
  const [people, setPeople] = useState<string[]>(shared?.shared_with ?? []);
  const peopleQuery = useSegmentPeople(true);
  const create = useCreateSharedSegment();
  const update = useUpdateSharedSegment();
  const makePrivate = useMakeSegmentPrivate();
  const busy = create.isPending || update.isPending || makePrivate.isPending;
  const error = create.error ?? update.error ?? makePrivate.error;

  const name = target.kind === 'private' ? target.tab.name : target.segment.name;
  // Someone else's segment, opened by an admin: it goes back to its owner.
  const ownerElse = shared && !shared.is_owner ? shared.owner_name : null;
  const ownerId = shared?.owner_user_id;
  const options = (peopleQuery.data?.people ?? [])
    .filter((p) => !p.is_you && p.id !== ownerId)
    .map((p) => ({
      value: p.id,
      label: p.role === 'buyer' || p.role === 'supplier' ? `${p.name} (${roleLabel(v, p.role)})` : p.name,
    }));

  const needsPeople = choice === 'people' && people.length === 0;

  async function save() {
    try {
      if (target.kind === 'private') {
        if (choice === 'private') { onDone?.({ kind: 'unchanged' }); onClose(); return; }
        const segment = await create.mutateAsync({
          name: target.tab.name,
          filter_state: target.tab.filter_state ?? null,
          visibility: choice,
          shared_with: choice === 'people' ? people : [],
          from_tab_id: target.tab.id,
        });
        notifications.show({ color: 'teal', title: 'Segment shared', message: `"${segment.name}" is now in Shared.` });
        onDone?.({ kind: 'shared', segment });
      } else if (choice === 'private') {
        const r = await makePrivate.mutateAsync(target.segment.id);
        notifications.show({
          color: 'teal',
          title: 'Sharing stopped',
          message: ownerElse
            ? `"${name}" is back in ${ownerElse}'s own segments.`
            : `"${name}" is back in your own segments.`,
        });
        onDone?.(target.segment.is_owner ? { kind: 'private', tabId: r.tab_id } : { kind: 'unchanged' });
      } else {
        const segment = await update.mutateAsync({
          id: target.segment.id,
          body: { visibility: choice, shared_with: choice === 'people' ? people : [] },
        });
        onDone?.({ kind: 'shared', segment });
      }
      onClose();
    } catch {
      /* shown below from the mutation's error */
    }
  }

  const filterCount = activeFilterCount(
    toFilters(target.kind === 'private' ? target.tab.filter_state : target.segment.filter_state),
  );

  return (
    <Stack gap="md">
      <Text size="sm">
        <Text span fw={600}>{name}</Text>
        <Text span c="dimmed">{filterCount === 0 ? ', no filters' : filterCount === 1 ? ', 1 filter' : `, ${filterCount} filters`}</Text>
      </Text>
      <Radio.Group value={choice} onChange={(c) => setChoice(c as Choice)} label="Who can open it">
        <Stack gap={10} mt={8}>
          <Radio value="private" label={ownerElse ? `Only ${ownerElse}` : 'Only me'} />
          <Radio value="everyone" label="Everyone in the organisation" />
          <Radio value="people" label="Chosen people" />
        </Stack>
      </Radio.Group>
      {choice === 'people' && (
        <MultiSelect
          label="People"
          placeholder={peopleQuery.isPending ? 'Loading people' : 'Choose people'}
          data={options}
          value={people}
          onChange={setPeople}
          searchable
          clearable
          nothingFoundMessage="Nobody matches"
          rightSection={peopleQuery.isPending ? <Loader size={14} /> : undefined}
          comboboxProps={{ withinPortal: true }}
          data-testid="share-people"
        />
      )}
      <Text size="xs" c="dimmed">
        {choice === 'private'
          ? 'Kept in your own segments. Nobody else sees it.'
          : `Sharing shares the filters, not the results. Everyone who opens it sees only the ${v.many('lead')} their own login can see.${
            choice === 'everyone' ? ` ${v.One('buyer')} and ${v.one('supplier')} logins only see it if chosen by name.` : ''
          }`}
      </Text>
      {peopleQuery.error && choice === 'people' && (
        <Alert color="orange" variant="light">{`People could not be loaded: ${peopleQuery.error.message}`}</Alert>
      )}
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={needsPeople}>Save</Button>
      </Group>
    </Stack>
  );
}
